import { createAction } from '@reduxjs/toolkit';

import { getFailurePayload } from 'modules/utils';

import * as actionTypes from './actionTypes';

/**
 * Two-step upload — asks `url` for a pre-signed destination, then posts the raw file there.
 * @param {{
 *  url: string,
 *  file: File,
 *  mediaId: string,
 *  additionalData: object,
 *  onRequest: (media: object) => void,
 *  onProgress: (event: ProgressEvent) => void,
 *  onSuccess: (media: object) => void,
 *  onError: (media: object) => void,
 * }} params
 */
export const uploadInTwoSteps = createAction(actionTypes.UPLOAD_IN_TWO_STEPS, (params) => ({ payload: params }));

/**
 * No reducer listens to this: upload state lives in the caller's form (via onRequest/onSuccess/
 * onError, tracked per mediaId), not in this slice. It's still dispatched so the outcome shows up
 * in Redux DevTools and in Sentry breadcrumbs (see sentryReduxEnhancer in configureStore.js).
 * @param {string} mediaId
 */
export const uploadInTwoStepsSuccess = createAction(actionTypes.UPLOAD_IN_TWO_STEPS_SUCCESS, (mediaId) => ({
  payload: { mediaId },
}));

/**
 * Same as `uploadInTwoStepsSuccess`: unreduced by design, dispatched only for observability.
 * @param {string} mediaId
 * @param {Error} error
 */
export const uploadInTwoStepsFailure = createAction(actionTypes.UPLOAD_IN_TWO_STEPS_FAILURE, (mediaId, error) => ({
  payload: { mediaId, error: getFailurePayload(error).payload.error },
  error: true,
}));

/**
 * One-step upload — base64-encodes the image and POSTs it straight to `url`, which answers with the stored media.
 * Used by customize (banner/logo/advertising)
 * @param {string} key
 * @param {string} url
 * @param {import('react-dropzone').FileWithPath} image
 * @param {(data: { id: string, fileData: string }) => void} onSuccess
 * @param {import('utils/upload').UploadLimits} [limits]
 */
export const uploadInOneStep = createAction(actionTypes.UPLOAD_IN_ONE_STEP, (key, url, image, onSuccess, limits = undefined) => ({
  payload: {
    key,
    request: { method: 'POST', url },
    image,
    limits,
    onSuccess,
  },
}));

/**
 * @param {string} key
 * @param {{ id: string, fileData: string }} data
 */
export const uploadInOneStepSuccess = createAction(actionTypes.UPLOAD_IN_ONE_STEP_SUCCESS, (key, data) => ({
  payload: { key, data },
}));

/**
 * @param {string} key
 * @param {Error} error
 */
export const uploadInOneStepFailure = createAction(actionTypes.UPLOAD_IN_ONE_STEP_FAILURE, (key, error) => ({
  payload: { key, error: getFailurePayload(error).payload.error },
  error: true,
}));
